import { TranslateService } from '@ngx-translate/core';
import { Injectable } from '@angular/core';

import { Category, ImageType, Price, Product } from '@spartacus/core';

import { ICategorySearch, IMedia, IProductCard, ISearchChild, ISearchData, LinkUtils } from '@seb/web-design-system/core';

import { DcpListResponse, RecipeMedia, RecipeSearchItem, RichAutocompleteResult } from '../models';
import { RecipeService } from '../services/recipe.service';
import { getMediaByFormat } from '../utils';
import { LinkMapper } from './link.mapper';

@Injectable({
  providedIn: 'root'
})
export class SearchMapper {
  constructor(private recipeService: RecipeService, private translateService: TranslateService, private readonly linkMaper: LinkMapper) {}

  mapRichAutocompleteResultToISearchData(
    auto: RichAutocompleteResult,
    displayTaxesIncluded,
    searchedValue: string,
    isNewPriceManagementActivated?: boolean,
    isHob?: boolean
  ): ISearchData {
    return {
      data: {
        categories: this.mapCategoriesToICategorySearch(auto.categories, auto.totalCategories),
        products: this.mapProductsOrAccessoriesToIProductCardSearch(
          auto.products as Product[],
          auto.totalProducts,
          `/search?target=product&query=${searchedValue}&text=${searchedValue}`,
          displayTaxesIncluded,
          isNewPriceManagementActivated,
          isHob
        ),
        accessories: this.mapProductsOrAccessoriesToIProductCardSearch(
          auto.accessories as Product[],
          auto.totalAccessories,
          `/search?target=accessoryProduct&query=${searchedValue}&text=${searchedValue}`,
          displayTaxesIncluded,
          isNewPriceManagementActivated
        )
      }
    };
  }

  mapRecipeSearchResultToISearchData(
    recipeSearchResult: DcpListResponse<RecipeSearchItem>,
    searchedValue: string
  ): ISearchData {
    return {
      data: {
        recipes: this.mapRecipesToIProductCardSearch(
          recipeSearchResult.content,
          recipeSearchResult.page.totalElements,
          `/search?target=recipes&query=${searchedValue}&text=${searchedValue}`
        )
      }
    };
  }

  private mapProductsOrAccessoriesToIProductCardSearch(
    products: Product[],
    totalProducts: number,
    allLink: string,
    displayTaxesIncluded: boolean,
    isNewPriceManagementActivated?: boolean,
    isHob?: boolean
  ): ISearchChild<IProductCard> {
    return {
      total: totalProducts,
      seeAllLink: allLink,
      items: products?.map((product) => ({
        ...this.formatPrice({
          price: product.price,
          discountedPrice: product.discountedPrice,
          recommendedPrice: product.recommendedPrice,
          strikethroughPrice: product.strikethroughPrice
        }, isNewPriceManagementActivated),
        title: product?.name,
        subtitle: product?.claim,
        media: getMediaByFormat(product?.primaryImages, 'thumbnail') ?? product?.images?.[ImageType.PRIMARY]?.['thumbnail'],
        url: this.linkMaper.getBrandUrl(product.brand, product?.url),
        indexFollow: product?.indexFollow,
        brandLogoTitle: isHob && !product?.isAccessory && product.brand ? this.getBrandLogoTitle(product.brand) : null,
        isTaxesIncluded: displayTaxesIncluded,
        code: product?.code ? product?.code : product?.url?.split('/').pop()
      }))
    };
  }

  getBrandLogoTitle(brand: string): IMedia {
    const logoBrand = brand.includes('HOB') ? brand.toLowerCase() : brand.toLowerCase();

    if (logoBrand) {
      return {
        url: `assets/icons/commons/icon-card-brand-${logoBrand}.svg`,
        altText: brand,
        mime: 'image/svg+xml'
      };
    } else {
      return null;
    }
  }

  private mapRecipesToIProductCardSearch(
    recipes: RecipeSearchItem[],
    totalRecipes: number,
    allLink: string
  ): ISearchChild<IProductCard> {
    return {
      total: totalRecipes,
      seeAllLink: allLink,
      items: recipes?.map((recipe) => {
        const coverMedia: RecipeMedia | undefined = this.recipeService.searchForCover(recipe.resourceMedias);
        const recipeDurationLabel: string = this.translateService.instant('recipes.blockTimer.minutes', {
          duration: recipe.durations?.totalTime
        });
        const baseUrl = recipe.seoUrl || `/${recipe.identifier.sourceSystem}/${recipe.title}/${recipe.topRecipeId}`;
        return {
          title: recipe.title,
          subtitle: `${recipeDurationLabel} - ${recipe.brand?.name}`,
          ...(coverMedia && {
            media: {
              url: coverMedia.media.thumbnail,
              mediaTitle: recipe.title
            }
          }),
          url: `/recipe/detail${LinkUtils.resolveUrlLink(baseUrl)}`
        };
      })
    };
  }

  private mapCategoriesToICategorySearch(
    categories: Category[],
    totalCategories: number
  ): ISearchChild<ICategorySearch> {
    return {
      total: totalCategories,
      items: categories?.map((category) => ({
        title: category.name,
        url: LinkMapper.decodeUrl(category.url)
      }))
    };
  }

  formatPrice(
    prices: { price?: Price, discountedPrice?: Price, recommendedPrice?: Price, strikethroughPrice?: boolean },
    isNewPriceManagementActivated: boolean) {
    return {
      discountedPrice:
        !!prices.discountedPrice && prices.discountedPrice?.value !== prices.price?.value
          ? prices.discountedPrice?.formattedValue
          : undefined,
      recommendedPrice:
        !!prices.recommendedPrice && prices.recommendedPrice?.value !== prices.price?.value && isNewPriceManagementActivated
          ? prices.recommendedPrice?.formattedValue
          : undefined,
      isDiscountWithRecommendedPrice: !isNewPriceManagementActivated ? !prices?.strikethroughPrice : false,
      price: prices?.price?.formattedValue
    };
  }
}
